import React, { useMemo, useState } from 'react';
import { buildSimpleSectionedSpec, buildAdvancedSectionedSpec } from '../../utils/alertsSpec';
import { TIMEFRAMES } from '../../lib/timeframes';
import SymbolSelection from '../SymbolSelection';
import RuleBuilder from '../bots/sectioned-builder/RuleBuilder';
import { Select, SelectContent, SelectItem } from '../ui/select';
import { useIndicatorMetadata } from '../../context/IndicatorMetadataContext';
import { createAlert } from '../../api/alertsApi';
import { toast } from 'sonner';

// Minimal UI atoms reused from existing
// Local minimal Button to avoid external UI deps
const Button = ({ children, variant = 'primary', ...props }) => (
  <button
    type="button"
    {...props}
    className={`px-3 py-1.5 text-sm rounded-md border ${
      variant === 'secondary'
  ? 'bg-muted text-textPrimary border-borderDefault hover:bg-muted'
        : 'bg-background text-textPrimary border-borderDefault hover:bg-muted'
    } ${props.className || ''}`}
  >
    {children}
  </button>
);

const TabButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
  className={`px-3 py-1.5 text-sm rounded-md border ${active ? 'bg-background text-textPrimary' : 'bg-muted text-textSecondary hover:text-textPrimary'} border-borderDefault`}
  >
    {children}
  </button>
);

// Small segmented toggle
const Segmented = ({ value, onChange, options }) => (
  <div className="inline-flex items-center p-0.5 bg-muted rounded-md border border-borderDefault">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={`px-2 py-0.5 text-xs rounded ${value === o.value ? 'bg-backgroundSurface text-textPrimary shadow-sm' : 'text-textSecondary hover:text-textPrimary'}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// Compact indicator picker (name only)
const MiniIndicatorPicker = ({ value, onChange, className = '' }) => {
  const { indicatorMetadata, isLoading, error } = useIndicatorMetadata();
  const canonicalizeParamName = (name) => name?.toLowerCase() === 'length' ? 'length' : name;

  const handleIndicatorChange = (indicatorName) => {
    const selectedIndicator = indicatorMetadata?.find((ind) => ind.name === indicatorName);
    if (!selectedIndicator) return;
    const newParams = { ...(value?.params || {}) };
    // migrate and fill defaults
    selectedIndicator.parameters.forEach((param) => {
      const key = canonicalizeParamName(param.name);
      Object.keys(newParams).forEach((existing) => {
        if (canonicalizeParamName(existing) === key && existing !== key) {
          newParams[key] = newParams[existing];
          delete newParams[existing];
        }
      });
      if (newParams[key] === undefined || newParams[key] === null) {
        newParams[key] = param.default_value;
      }
    });
    // drop obsolete params
    Object.keys(newParams).forEach((k) => {
      if (!selectedIndicator.parameters.find((p) => canonicalizeParamName(p.name) === canonicalizeParamName(k))) delete newParams[k];
    });

    onChange({
      type: 'indicator',
      name: indicatorName,
      output: selectedIndicator.outputs?.[0] || value?.output,
      params: newParams,
      input: value?.input || { type: 'price', source: 'close' },
      timeframe: value?.timeframe,
    });
  };

  if (isLoading) return <div className={`text-xs text-textSecondary ${className}`}>Loading…</div>;
  if (error) return <div className={`text-xs text-destructive ${className}`}>Failed to load indicators</div>;

  const selected = indicatorMetadata?.find((ind) => ind.name?.toLowerCase() === (value?.name || '').toLowerCase());

  return (
    <div className={className}>
      <Select
        onValueChange={handleIndicatorChange}
        value={selected?.name || ''}
        placeholder="Select indicator"
        searchable
        searchPlaceholder="Search indicators…"
        className="w-full"
        itemHeight={30}
        maxDropdownHeight={240}
      >
        <SelectContent>
          {indicatorMetadata?.map((indicator) => (
            <SelectItem key={indicator.name} value={indicator.name}>
              {indicator.display_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Optional quick Period control if indicator exposes 'length' or 'period' */}
      {selected && (() => {
        const params = selected.parameters || [];
        const periodParam = params.find(p => ['length', 'period'].includes((p.name || '').toLowerCase()) && (p.parameter_type === 'integer' || p.parameter_type === 'float'));
        if (!periodParam) return null;
        const currentVal = value?.params?.[canonicalizeParamName(periodParam.name)] ?? periodParam.default_value;
        return (
          <div className="mt-2 flex items-center gap-2">
            <label className="text-xs text-textSecondary min-w-[48px]">Period</label>
            <input
              type="number"
              min={periodParam.min_value}
              max={periodParam.max_value}
              step={periodParam.step || 1}
              value={currentVal}
              onChange={(e) => {
                const val = periodParam.parameter_type === 'integer' ? parseInt(e.target.value || '0', 10) : parseFloat(e.target.value || '0');
                const nextParams = { ...(value?.params || {}) };
                nextParams[canonicalizeParamName(periodParam.name)] = val;
                onChange({ ...(value || {}), type: 'indicator', name: selected.name, params: nextParams, input: value?.input || { type: 'price', source: 'close' }, timeframe: value?.timeframe, output: value?.output });
              }}
              className="w-24 border border-borderDefault bg-backgroundSurface rounded px-2 py-1 text-xs"
            />
          </div>
        );
      })()}
    </div>
  );
};

// Simple rule row editor
const SimpleRuleRow = ({ value, onChange }) => {
  const update = (patch) => onChange({ ...value, ...patch });
  const lhsType = value?.lhs?.type === 'indicator' ? 'indicator' : 'price';
  const rhsType = typeof value?.rhs === 'number' ? 'number' : (value?.rhs?.type || 'number');

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-3 w-full">
      {/* LHS column */}
      <div className="space-y-2 min-w-0">
        <Segmented
          value={lhsType}
          onChange={(t) => {
            if (t === 'price') update({ lhs: { type: 'price', source: 'close' } });
            else update({ lhs: value?.lhs?.type === 'indicator' ? value.lhs : { type: 'indicator', name: 'ema', params: { length: 21 }, input: { type: 'price', source: 'close' } } });
          }}
          options={[{ label: 'Price', value: 'price' }, { label: 'Indicator', value: 'indicator' }]}
        />
        {lhsType === 'indicator' && (
          <MiniIndicatorPicker
            value={value.lhs}
            onChange={(next) => update({ lhs: next })}
            className="w-full"
          />
        )}
      </div>

      {/* Operator column */}
      <div className="flex flex-col items-center gap-2">
        <select
          value={value?.op || 'crosses_above'}
          onChange={(e) => update({ op: e.target.value })}
          className="min-w-[160px] border border-borderDefault bg-backgroundSurface rounded px-2 py-1 text-sm"
        >
          <option value="crosses_above">crosses above</option>
          <option value="crosses_below">crosses below</option>
          <option value="greater_than">greater than</option>
          <option value="less_than">less than</option>
          <option value="equals">equals</option>
          <option value="not_equals">not equals</option>
        </select>
      </div>

      {/* RHS column */}
      <div className="space-y-2 min-w-0">
        <Segmented
          value={rhsType}
          onChange={(t) => {
            if (t === 'number') update({ rhs: 0 });
            else if (t === 'price') update({ rhs: { type: 'price', source: 'close' } });
            else update({ rhs: (value?.rhs && typeof value.rhs === 'object' && value.rhs.type === 'indicator') ? value.rhs : { type: 'indicator', name: 'ema', params: { length: 50 }, input: { type: 'price', source: 'close' } } });
          }}
          options={[{ label: 'Number', value: 'number' }, { label: 'Price', value: 'price' }, { label: 'Indicator', value: 'indicator' }]}
        />
        {rhsType === 'number' && (
          <input
            type="number"
            step="any"
            value={Number.isFinite(value.rhs) ? value.rhs : 0}
            onChange={(e) => update({ rhs: parseFloat(e.target.value || '0') })}
            className="w-40 border border-borderDefault bg-backgroundSurface rounded px-2 py-1 text-sm"
          />
        )}
        {rhsType === 'indicator' && (
          <MiniIndicatorPicker
            value={value.rhs}
            onChange={(next) => update({ rhs: next })}
            className="w-full"
          />
        )}
      </div>
    </div>
  );
};

export default function AlertsWidget({
  initialName = '',
  initialSymbol,
  initialTimeframe = 'M5',
  initialTab = 'simple', // 'simple' | 'advanced'
  initialAdvSpec, // optional preloaded advanced spec (sectioned)
  initialSimpleRules, // optional preloaded simple rules (for 'entry' style specs)
  initialIsActive = true,
  initialPriceSource = 'AUTO',
  initialTriggerMode = 'RECURRING', // 'ONCE' | 'RECURRING'
  initialCooldownSeconds = 0,
  initialExpiresAt, // ISO string
  submitLabel, // optional custom button label
  watchlist,
  accountId,
  onBuild, // (payload) => void — caller will send to API
}) {
  const [tab, setTab] = useState(initialTab || 'simple');
  const [name, setName] = useState(initialName);
  const [symbol, setSymbol] = useState(initialSymbol);
  const [timeframe, setTimeframe] = useState(initialTimeframe);
  const [creating, setCreating] = useState(false);
  const [isActive, setIsActive] = useState(Boolean(initialIsActive));
  const [priceSource, setPriceSource] = useState(initialPriceSource || 'AUTO');
  const [triggerMode, setTriggerMode] = useState(initialTriggerMode || 'RECURRING');
  const [cooldownSeconds, setCooldownSeconds] = useState(Number(initialCooldownSeconds || 0));
  // datetime-local wants 'YYYY-MM-DDTHH:mm' in local time
  const toLocalInput = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      const y = d.getFullYear();
      const m = pad(d.getMonth() + 1);
      const da = pad(d.getDate());
      const h = pad(d.getHours());
      const mi = pad(d.getMinutes());
      return `${y}-${m}-${da}T${h}:${mi}`;
    } catch { return ''; }
  };
  const [expiresAtLocal, setExpiresAtLocal] = useState(toLocalInput(initialExpiresAt));

  // Simple mode state
  const [rules, setRules] = useState(
    Array.isArray(initialSimpleRules) && initialSimpleRules.length > 0
      ? initialSimpleRules
      : [
          {
            lhs: { type: 'indicator', name: 'ema', params: { length: 21 }, input: { type: 'price', source: 'close' } },
            op: 'crosses_above',
            rhs: { type: 'indicator', name: 'ema', params: { length: 50 }, input: { type: 'price', source: 'close' } },
          },
        ]
  );

  React.useEffect(() => {
    if (Array.isArray(initialSimpleRules) && initialSimpleRules.length > 0) {
      setRules(initialSimpleRules);
    }
  }, [initialSimpleRules]);

  // Advanced mode state – independent from bots builder; we instantiate a minimal grouped spec shape
  const [advSpec, setAdvSpec] = useState({
    timeframe,
    entry_long: { type: 'group', op: 'AND', clauses: [] },
    entry_short: { type: 'group', op: 'AND', clauses: [] },
    exit_long: { type: 'group', op: 'OR', clauses: [] },
    exit_short: { type: 'group', op: 'OR', clauses: [] },
  });

  // Initialize advanced spec from props when provided
  React.useEffect(() => {
    if (initialAdvSpec && typeof initialAdvSpec === 'object') {
      setAdvSpec({
        entry_long: { type: 'group', op: 'AND', clauses: [], ...(initialAdvSpec.entry_long || {}) },
        entry_short: { type: 'group', op: 'AND', clauses: [], ...(initialAdvSpec.entry_short || {}) },
        exit_long: { type: 'group', op: 'OR', clauses: [], ...(initialAdvSpec.exit_long || {}) },
        exit_short: { type: 'group', op: 'OR', clauses: [], ...(initialAdvSpec.exit_short || {}) },
        timeframe: initialAdvSpec.timeframe || timeframe,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAdvSpec]);

  // Keep advanced spec base timeframe in sync
  React.useEffect(() => {
    setAdvSpec((s) => ({ ...s, timeframe }));
  }, [timeframe]);

  // Name is optional; we'll auto-generate if left empty
  const canSave = useMemo(() => Boolean(symbol && timeframe && accountId && (tab === 'simple' ? rules.length > 0 : true)), [symbol, timeframe, accountId, tab, rules.length]);

  const generateDefaultName = () => {
    const ts = new Date();
    const tsStr = `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, '0')}-${String(ts.getDate()).padStart(2, '0')} ${String(ts.getHours()).padStart(2, '0')}:${String(ts.getMinutes()).padStart(2, '0')}`;
    const parts = [];
    if (symbol) parts.push(symbol);
    if (timeframe) parts.push(timeframe);
    parts.push(tab === 'simple' ? 'Alert' : 'Adv Alert');
    return `${parts.join(' ')} ${tsStr}`;
  };

  const buildPayload = async () => {
    const finalName = (name && name.trim()) ? name.trim() : generateDefaultName();
    const expires_at = expiresAtLocal ? new Date(expiresAtLocal).toISOString() : null;
    if (tab === 'simple') {
      const sectioned_spec = buildSimpleSectionedSpec({ timeframe, rules });
      return { name: finalName, symbol, timeframe, sectioned_spec, is_active: isActive, account_id: accountId, price_source: priceSource, trigger_mode: triggerMode, cooldown_seconds: triggerMode === 'RECURRING' ? Number(cooldownSeconds || 0) : undefined, expires_at };
    }
    const sectioned_spec = await buildAdvancedSectionedSpec(advSpec, { timeframe });
    return { name: finalName, symbol, timeframe, sectioned_spec, is_active: isActive, account_id: accountId, price_source: priceSource, trigger_mode: triggerMode, cooldown_seconds: triggerMode === 'RECURRING' ? Number(cooldownSeconds || 0) : undefined, expires_at };
  };

  return (
  <div className="w-full max-w-full rounded-lg border border-borderDefault bg-backgroundSurface p-3 space-y-3 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Alert name (optional; auto-generated if empty)"
          className="flex-1 border border-borderDefault bg-backgroundSurface rounded px-3 py-2 text-sm"
        />
      </div>

      {/* Symbol + timeframe */}
      <div className="flex flex-wrap items-center gap-3">
        <SymbolSelection selectedSymbol={symbol} setSelectedSymbol={setSymbol} watchlist={watchlist} accountId={accountId} />
        <div className="flex items-center gap-2">
          <span className="text-sm text-textSecondary">Timeframe</span>
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            className="border border-borderDefault bg-backgroundSurface rounded px-2 py-1 text-sm"
          >
            {TIMEFRAMES.map(tf => <option key={tf} value={tf}>{tf}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-textSecondary">Price source</span>
          <select
            value={priceSource}
            onChange={(e) => setPriceSource(e.target.value)}
            className="border border-borderDefault bg-backgroundSurface rounded px-2 py-1 text-sm"
          >
            <option value="AUTO">Auto</option>
            <option value="MARKET_DATA">Market data</option>
            <option value="BROKER">Broker</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2">
        <TabButton active={tab === 'simple'} onClick={() => setTab('simple')}>Simple</TabButton>
        <TabButton active={tab === 'advanced'} onClick={() => setTab('advanced')}>Advanced</TabButton>
      </div>

      {/* Bodies */}
      {tab === 'simple' ? (
        <div className="space-y-2">
      {rules.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
        <SimpleRuleRow value={r} onChange={(nv) => setRules((arr) => arr.map((x, idx) => idx === i ? nv : x))} />
              <button
                type="button"
                className="text-xs px-2 py-1 rounded border border-borderDefault bg-muted"
                onClick={() => setRules((arr) => arr.filter((_, idx) => idx !== i))}
              >Remove</button>
            </div>
          ))}
          <Button type="button" variant="secondary" onClick={() => setRules((arr) => [...arr, { lhs: { type: 'price', source: 'close' }, op: 'greater_than', rhs: 0 }])}>+ Add rule</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="text-xs font-semibold mb-1 text-textSecondary">Entry Long</div>
            <RuleBuilder section="entry_long" spec={advSpec.entry_long} onChange={(upd) => setAdvSpec((s) => ({ ...s, entry_long: typeof upd === 'function' ? upd(s.entry_long) : upd }))} baseTimeframe={timeframe} />
          </div>
          <div>
            <div className="text-xs font-semibold mb-1 text-textSecondary">Entry Short</div>
            <RuleBuilder section="entry_short" spec={advSpec.entry_short} onChange={(upd) => setAdvSpec((s) => ({ ...s, entry_short: typeof upd === 'function' ? upd(s.entry_short) : upd }))} baseTimeframe={timeframe} />
          </div>
          <div>
            <div className="text-xs font-semibold mb-1 text-textSecondary">Exit Long</div>
            <RuleBuilder section="exit_long" spec={advSpec.exit_long} onChange={(upd) => setAdvSpec((s) => ({ ...s, exit_long: typeof upd === 'function' ? upd(s.exit_long) : upd }))} baseTimeframe={timeframe} />
          </div>
          <div>
            <div className="text-xs font-semibold mb-1 text-textSecondary">Exit Short</div>
            <RuleBuilder section="exit_short" spec={advSpec.exit_short} onChange={(upd) => setAdvSpec((s) => ({ ...s, exit_short: typeof upd === 'function' ? upd(s.exit_short) : upd }))} baseTimeframe={timeframe} />
          </div>
        </div>
      )}

      {/* Footer */}
      {/* Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
        <div className="flex items-center gap-3">
          <label className="text-sm text-textSecondary">Active</label>
          <input type="checkbox" checked={!!isActive} onChange={(e) => setIsActive(e.target.checked)} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-textSecondary">Trigger</span>
            <select
              value={triggerMode}
              onChange={(e) => setTriggerMode(e.target.value)}
              className="border border-borderDefault bg-backgroundSurface rounded px-2 py-1 text-sm"
            >
              <option value="RECURRING">RECURRING</option>
              <option value="ONCE">ONCE</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-textSecondary">Cooldown (s)</span>
            <input
              type="number"
              min={0}
              step={1}
              disabled={triggerMode !== 'RECURRING'}
              value={cooldownSeconds}
              onChange={(e) => setCooldownSeconds(parseInt(e.target.value || '0', 10))}
              className="w-28 border border-borderDefault bg-backgroundSurface rounded px-2 py-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-textSecondary">Expires</span>
            <input
              type="datetime-local"
              value={expiresAtLocal}
              onChange={(e) => setExpiresAtLocal(e.target.value)}
              className="border border-borderDefault bg-backgroundSurface rounded px-2 py-1 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          type="button"
          disabled={!canSave || creating}
          onClick={async () => {
            try {
              setCreating(true);
              const payload = await buildPayload();
              if (onBuild) {
                await onBuild(payload);
              } else {
                // Fallback: create directly if no handler provided
                await createAlert(payload);
              }
              toast.success('Alert created');
            } catch (err) {
              toast.error(err?.message || 'Failed to create alert');
            } finally {
              setCreating(false);
            }
          }}
        >
          {creating ? 'Saving…' : (submitLabel || (tab === 'simple' && !initialAdvSpec ? 'Create alert' : 'Save changes'))}
        </Button>
      </div>
    </div>
  );
}
